import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  CardNotification,
  Heading,
  Pager,
  Select,
  SkeletonBase,
  Text,
} from "@foris/avocado-suite";
import { Header } from "@common/components";
import { Types } from "../../context/search.reducer";
import { Types as RequestTypes } from "../../context/request.reducer";
import { Types as SavedRequestTypes } from "../../context/savedRequest.reducer";
import { BaseQuery, Scenario, ScenarioStatusEnum } from "@models/ISchema";
import { useApolloClient } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { BookingContext } from "@modules/booking/context/BookingContext";
import Layout from "@common/layout";
import scenariosBaseQuery from "../../graphql/scenariosBase.query.graphql";
import useGetAppBookings from "@modules/booking/hooks/useGetAppBookings";
import BookingCard from "@modules/booking/components/booking-card";
import useBookingContextUrl from "@modules/booking/hooks/useBookingContextUrl";
import { useIsMobile } from "@common/hooks/useIsMobile";
import useContextHasIntervals from "@modules/booking/hooks/useContextHasIntervals";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import styles from "./bookingListApp.module.scss";
import BookingListFilters from "@modules/booking/components/booking-list-filters";

interface ScenarioOption extends Scenario {
  value: string;
  label: string;
}

const BookingListApp = () => {
  const history = useHistory();
  const client = useApolloClient();
  const { isMobile } = useIsMobile();
  const { t } = useTranslation();
  const { dispatch } = useContext(BookingContext);
  const { adaptedHomeUrl } = useNavigationUrl();
  const { contextUrl, queryParams } = useBookingContextUrl();
  const [isLoadingScenarios, setIsLoadingScenarios] = useState(false);
  const [scenarios, setScenarios] = useState<ScenarioOption[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<ScenarioOption | null>(null);
  const savedCampus = queryParams.get("campus");

  const urlQueryParams = `?scenario=${selectedScenario?.id}&origin=${selectedScenario?.productiveOrigin?.id}&from=app`;

  const {
    isLoading,
    getBookings,
    bookings,
    pageInfo,
    sizePerPage,
    activeFilter,
    totalActive,
    totalExpired,
  } = useGetAppBookings();

  const { intervals, isScenarioValid } = useContextHasIntervals({
    externalScenario: selectedScenario?.id,
    canFetch: !!selectedScenario?.id,
  });

  const getScenarios = async () => {
    setIsLoadingScenarios(true);
    try {
      const { data, loading, errors } = await client.query({
        query: scenariosBaseQuery,
        variables: {
          filter: {
            status: ScenarioStatusEnum.Productive,
          },
        },
      });

      if (!loading && errors?.length) console.error("[campus]", errors);

      if (!loading && data?.base) {
        const base: BaseQuery = data.base;
        const options = base.scenarios
          .map(val => {
            return { label: val.name, value: val.id, ...val };
          })
          ?.filter(val => !!val.productiveOrigin);

        setScenarios(options);

        if (savedCampus) {
          const scenario = options.find(val => val.id === savedCampus);

          if (scenario) {
            setSelectedScenario(scenario);
            getBookings(scenario as Scenario, 1);
          }
        }
      }

      setIsLoadingScenarios(false);
    } catch (error) {
      console.error("[campus]", error);
      setIsLoadingScenarios(false);
    }
  };

  useEffect(() => {
    if (!scenarios.length && !isLoadingScenarios) {
      getScenarios();
    }
  }, []);

  const handleResetStore = () => {
    dispatch({ type: Types.ClearSearch, payload: null });
    dispatch({ type: RequestTypes.ClearRequest, payload: null });
    dispatch({ type: SavedRequestTypes.clearSavedData, payload: null });
  };

  return (
    <Layout contextSearch={false}>
      <Header
        breadcrumbItems={[
          { title: t("home.breadcrumb.current"), path: adaptedHomeUrl() },
          { title: t("booking.title") },
        ]}
        title={t("booking.title")}
      />

      <div className={styles.bookingListApp}>
        <div className={styles.bookingListApp_scenarioBox}>
          <Select
            className={styles.selector}
            label={t("booking.booking-list-app.scenario-selector.label")}
            placeholder={t("booking.booking-list-app.scenario-selector.placeholder")}
            options={scenarios}
            isLoading={isLoadingScenarios}
            value={selectedScenario}
            disabled={isLoading}
            onChange={(option: ScenarioOption | null) => {
              setSelectedScenario(option);
              getBookings(option as Scenario, 1);
            }}
          />

          {intervals !== null && !isScenarioValid && (
            <CardNotification
              title={t("booking.booking-list-app.notifications.scenario-outside-range.title")}
              state="warning"
            >
              <Text type="sm">
                {t("booking.booking-list-app.notifications.scenario-outside-range.description")}
              </Text>
            </CardNotification>
          )}

          <Button
            className={styles.requestBooking}
            disabled={isLoadingScenarios || isLoading || !selectedScenario?.id || !isScenarioValid}
            onClick={() => {
              handleResetStore();
              history.push(`/booking/search/${contextUrl}${urlQueryParams}`);
            }}
          >
            {t("booking.booking-list-app.scenario-selector.action-btn")}
          </Button>
        </div>

        <div className={styles.bookingListApp_content}>
          {!!(isLoading || !!selectedScenario || (!isLoading && !!pageInfo?.total)) && (
            <>
              <Heading className={styles.contentTitle} type={isMobile ? "h3" : "h2"}>
                {isLoading ? (
                  <SkeletonBase width="80%" height={24} />
                ) : (
                  <>
                    {t("booking.booking-list-app.results-title", {
                      count: pageInfo?.total ?? 0,
                    })}{" "}
                    {selectedScenario?.label || ""}
                  </>
                )}
              </Heading>

              <BookingListFilters
                totalBookings={totalActive + totalExpired}
                activeCount={totalActive}
                expiredCount={totalExpired}
                activeFilter={activeFilter}
                isLoading={isLoading}
                handleFilterChange={filter => {
                  if (filter !== activeFilter) {
                    getBookings(selectedScenario, 1, sizePerPage, filter, true);
                  }
                }}
              />
            </>
          )}

          <div className={styles.bookings}>
            {!isLoading && selectedScenario && pageInfo?.total === 0 && (
              <CardNotification
                title={t("booking.booking-list-app.notifications.empty-results.title")}
                state="info"
                outlined
              >
                <Text type="sm">
                  {t("booking.booking-list-app.notifications.empty-results.description")}{" "}
                  {`"${selectedScenario?.label || ""}"`}.
                </Text>
              </CardNotification>
            )}

            {isLoading &&
              Array(5)
                .fill(0)
                .map((_, index) => <BookingCard key={index} isLoading />)}

            {!isLoading &&
              (bookings ?? [])?.map(booking => (
                <BookingCard
                  key={booking.id}
                  booking={booking}
                  onClick={() =>
                    history.push(`/booking/detail/${contextUrl}/${booking?.id}${urlQueryParams}`)
                  }
                />
              ))}

            {!isLoading && !!pageInfo?.total && (
              <Pager
                total={pageInfo?.total ?? 0}
                page={pageInfo?.page ?? 1}
                size={sizePerPage}
                onChange={page => {
                  getBookings(selectedScenario, page, sizePerPage, activeFilter, true);
                }}
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BookingListApp;
